/**
 * circle-empty-state — what the Discover deck shows, in priority order.
 *
 * A failed load is NOT an empty circle: offline, an active circle used to read
 * as "nobody shared anything yet". And a filter that hides every sound is not
 * an empty circle either: the sounds are there, a chip hides them.
 *
 * "Empty" needs a feed that really LOADED: offline, react-query pauses the
 * fetch instead of failing it (no error, no data, not fetching), and that
 * must read as a failed load, not as 0 sounds.
 *
 * The hidden-all verdict carries its count, so the screen never has to default
 * one: a "0" the server did not say is never shown.
 */
export type DiscoverDeckState =
  | { kind: 'loading' }
  | { kind: 'hidden-all'; hiddenCount: number }
  | { kind: 'error' }
  | { kind: 'empty' }
  | { kind: 'deck' };

export const discoverDeckState = ({
  isLoading,
  hiddenCount,
  isError,
  loaded,
  count,
}: {
  isLoading: boolean;
  /** Sounds the style filter hid, as the server says it — `undefined` until the backend sends it. */
  hiddenCount: number | undefined;
  isError: boolean;
  /** At least one page of the feed came back from the server. */
  loaded: boolean;
  /** Sounds in the feed once the style filter is applied. */
  count: number;
}): DiscoverDeckState => {
  if (isLoading) return { kind: 'loading' };
  if (count === 0 && typeof hiddenCount === 'number' && hiddenCount > 0) {
    return { kind: 'hidden-all', hiddenCount };
  }
  if (isError || !loaded) return { kind: 'error' };
  if (count === 0) return { kind: 'empty' };
  return { kind: 'deck' };
};
